import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, requestBlob } from '../lib/api';
import { qk } from './keys';
import type {
  CommissionRate,
  LessonEarning,
  ListLessonEarningsQuery,
  ListTutorApplicationsQuery,
  Paginated,
  RevealedPayoutAccount,
  TutorApplication,
  TutorApplicationListItem,
} from '../types/api';

// -------------------------------------------------------------- the queue --

export function useTutorApplications(query: ListTutorApplicationsQuery) {
  return useQuery({
    queryKey: qk.tutorApplications(query),
    queryFn: () =>
      api.get<Paginated<TutorApplicationListItem>>('/admin/tutor-applications', query),
    placeholderData: (prev) => prev,
    // A shared worklist: two reviewers clearing it should not be looking at
    // different queues for a minute. Same argument as the trial pipeline.
    refetchInterval: 30_000,
  });
}

export function useTutorApplication(tutorId: string | undefined) {
  return useQuery({
    queryKey: qk.tutorApplication(tutorId ?? ''),
    queryFn: () => api.get<TutorApplication>(`/admin/tutor-applications/${tutorId}`),
    enabled: Boolean(tutorId),
  });
}

/**
 * Every decision invalidates the same three things, so they are listed once.
 *
 * The users list goes with them because approving a tutor grants the badge
 * and un-approving takes it away, and that badge is a column there.
 */
function useDecision<TBody>(
  path: (tutorId: string) => string,
): ReturnType<typeof useMutation<TutorApplication, Error, { tutorId: string } & TBody>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tutorId, ...body }: { tutorId: string } & TBody) =>
      api.post<TutorApplication>(path(tutorId), body),
    onSuccess: (application) => {
      void qc.invalidateQueries({ queryKey: qk.tutorApplicationsAll });
      void qc.invalidateQueries({ queryKey: qk.usersAll });
      void qc.invalidateQueries({ queryKey: qk.tutorCommissions(application.id) });
      qc.setQueryData(qk.tutorApplication(application.id), application);
    },
  });
}

export const useApproveTutorApplication = () =>
  useDecision<{ commissionPercent: number; note?: string }>(
    (id) => `/admin/tutor-applications/${id}/approve`,
  );

export const useRequestTutorChanges = () =>
  useDecision<{ note: string; fields?: string[] }>(
    (id) => `/admin/tutor-applications/${id}/request-changes`,
  );

export const useRejectTutorApplication = () =>
  useDecision<{ note: string }>((id) => `/admin/tutor-applications/${id}/reject`);

export const useSuspendTutor = () =>
  useDecision<{ note: string }>((id) => `/admin/tutors/${id}/suspend`);

export const useReinstateTutor = () =>
  useDecision<{ note: string }>((id) => `/admin/tutors/${id}/reinstate`);

// ------------------------------------------------ the two sensitive reads --

/**
 * Opens one ID document in a new tab.
 *
 * Not a query: every read writes a `tutor.document_viewed` audit row, so it
 * must happen when somebody asks rather than whenever React Query decides to
 * refetch. The object URL is revoked once the tab has had it — a minute is
 * generous and the blob is somebody's identity document.
 */
export function useViewTutorDocument() {
  return useMutation({
    mutationFn: async ({ tutorId, documentId }: { tutorId: string; documentId: string }) => {
      const blob = await requestBlob(`/admin/tutors/${tutorId}/documents/${documentId}/file`);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return url;
    },
  });
}

/** Masked. `reveal` is the audited call and is deliberately separate below. */
export function useTutorPayoutAccount(tutorId: string | undefined) {
  return useQuery({
    queryKey: qk.tutorPayoutAccount(tutorId ?? '', false),
    queryFn: () => api.get<RevealedPayoutAccount>(`/admin/tutors/${tutorId}/payout-account`),
    enabled: Boolean(tutorId),
    // A tutor with no payout account answers 404, which is an ordinary
    // state here rather than a failure worth retrying.
    retry: false,
  });
}

/**
 * The full IBAN, wallet number or InstaPay address.
 *
 * A mutation rather than a query for the same reason the document is: the
 * read writes `tutor.payout_account_revealed`, so it happens when an admin
 * presses Reveal and not a moment before.
 */
export function useRevealTutorPayoutAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tutorId }: { tutorId: string }) =>
      api.get<RevealedPayoutAccount>(`/admin/tutors/${tutorId}/payout-account`, {
        reveal: true,
      }),
    onSuccess: (_, { tutorId }) => {
      // The audit log just gained a row, and this screen may be showing it.
      void qc.invalidateQueries({ queryKey: qk.auditAll });
      void qc.invalidateQueries({ queryKey: qk.tutorPayoutAccountAll(tutorId) });
    },
  });
}

// --------------------------------------------------------- the commission --

export function useTutorCommissions(tutorId: string | undefined) {
  return useQuery({
    queryKey: qk.tutorCommissions(tutorId ?? ''),
    queryFn: () => api.get<CommissionRate[]>(`/admin/tutors/${tutorId}/commissions`),
    enabled: Boolean(tutorId),
  });
}

export function useSetTutorCommission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      tutorId,
      ...body
    }: {
      tutorId: string;
      commissionPercent: number;
      effectiveFrom?: string;
      note: string;
    }) => api.put<CommissionRate[]>(`/admin/tutors/${tutorId}/commission`, body),
    onSuccess: (rates, { tutorId }) => {
      qc.setQueryData(qk.tutorCommissions(tutorId), rates);
      void qc.invalidateQueries({ queryKey: qk.tutorApplicationsAll });
      void qc.invalidateQueries({ queryKey: qk.auditAll });
    },
  });
}

// ------------------------------------------------------ no-show earnings --

export function useLessonEarnings(query: ListLessonEarningsQuery) {
  return useQuery({
    queryKey: qk.lessonEarnings(query),
    queryFn: () => api.get<Paginated<LessonEarning>>('/admin/lesson-earnings', query),
    placeholderData: (prev) => prev,
  });
}

/**
 * Confirm or void a tutor-reported no-show.
 *
 * Only the tutor can report one, so the earning waits here until somebody
 * checks the call log against the report. Neither decision touches the
 * amounts — only the status moves.
 */
export function useDecideLessonEarning() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      decision,
      note,
    }: {
      sessionId: string;
      decision: 'confirm' | 'void';
      note?: string;
    }) => api.post<LessonEarning>(`/admin/lesson-earnings/${sessionId}/${decision}`, { note }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.lessonEarningsAll });
      void qc.invalidateQueries({ queryKey: qk.auditAll });
    },
  });
}
